import { getSupportedDevicesSync } from "./supportedDevicesStore";

export type XingshanLightingOpenStatus =
  | "Close"
  | "Open"
  | "OpenUp"
  | "OpenDown";

export type XingshanDoubleLightingPositionLocked = "upper" | "lower";

/** 星闪键盘 equipment 能力字段（来自 supportedDevices 配置，连接时写入 equipment） */
export type XingshanKeyboardEquipment =
  | {
      hasDoubleLighting?: boolean;
      hasDotMatrixScreen?: boolean;
      vendorId?: number;
      productId?: number;
      doubleLightingPositionLocked?: XingshanDoubleLightingPositionLocked;
    }
  | null
  | undefined;

const XINGSHAN_LIGHTING_OPEN_STORAGE_PREFIX =
  "xingshan-keyboard-lighting-open:";

function isXingshanLightingOpenStatus(
  value: unknown
): value is XingshanLightingOpenStatus {
  const v = String(value ?? "");
  return v === "Close" || v === "Open" || v === "OpenUp" || v === "OpenDown";
}

/** 内存 equipment 丢能力字段时，按 VID/PID 回查配置表 */
function resolveXingshanCatalogRow(
  equipment: Record<string, unknown>
): Record<string, unknown> | null {
  const vendorId = Number(equipment.vendorId);
  const productId = Number(equipment.productId);
  if (!Number.isFinite(vendorId) || !Number.isFinite(productId)) return null;
  const kb = getSupportedDevicesSync().keyboard as Array<
    Record<string, unknown>
  >;
  return (
    kb.find(
      (row) =>
        String(row.solutionRovider ?? "") === "xingshan" &&
        row.isSupported !== false &&
        Number(row.vendorId) === vendorId &&
        Number(row.productId) === productId
    ) ?? null
  );
}

/** 配置表声明的双灯位机型（含仅上/仅下锁定款） */
export function isXingshanDoubleLightingEquipment(
  equipment?: XingshanKeyboardEquipment | Record<string, unknown>
): boolean {
  if (!equipment || typeof equipment !== "object") return false;
  const flag = (equipment as { hasDoubleLighting?: boolean }).hasDoubleLighting;
  if (flag === true) return true;
  if (flag === false) return false;
  return (
    resolveXingshanCatalogRow(equipment as Record<string, unknown>)
      ?.hasDoubleLighting === true
  );
}

/** 灯位锁定：仅下灯 / 仅上灯，不可切换全灯或另一层 */
export function getXingshanDoubleLightingPositionLocked(
  equipment?: XingshanKeyboardEquipment | Record<string, unknown>
): XingshanDoubleLightingPositionLocked | null {
  if (!equipment || typeof equipment !== "object") return null;
  const locked = (equipment as { doubleLightingPositionLocked?: unknown })
    .doubleLightingPositionLocked;
  if (locked === "upper" || locked === "lower") return locked;
  const fromCatalog = resolveXingshanCatalogRow(
    equipment as Record<string, unknown>
  )?.doubleLightingPositionLocked;
  return fromCatalog === "upper" || fromCatalog === "lower"
    ? fromCatalog
    : null;
}

function openStatusForLockedDoubleLighting(
  locked: XingshanDoubleLightingPositionLocked
): XingshanLightingOpenStatus {
  return locked === "lower" ? "OpenDown" : "OpenUp";
}

/** 将 open 收敛到锁定灯位（Close 保持不变） */
export function applyXingshanDoubleLightingPositionLock(
  open: unknown,
  equipment?: XingshanKeyboardEquipment | Record<string, unknown>
): XingshanLightingOpenStatus {
  const value = String(open ?? "");
  if (value === "Close") return "Close";
  const locked = getXingshanDoubleLightingPositionLocked(equipment);
  if (locked) return openStatusForLockedDoubleLighting(locked);
  if (
    value === "OpenUp" ||
    value === "OpenDown" ||
    value === "Open" ||
    value === "Close"
  ) {
    return value as XingshanLightingOpenStatus;
  }
  return "Open";
}

/** 当前设备是否展示上下灯位切换 Tab */
export function supportsXingshanDoubleLightingPosition(
  equipment?: XingshanKeyboardEquipment | Record<string, unknown>
): boolean {
  return (
    isXingshanDoubleLightingEquipment(equipment) &&
    getXingshanDoubleLightingPositionLocked(equipment) == null
  );
}

/** 双灯位机型使用 DoubleLighting，其余星闪键盘默认 SingleLighting */
export function resolveXingshanKeyboardLightingLamp(
  equipment?: XingshanKeyboardEquipment | Record<string, unknown>
): "SingleLighting" | "DoubleLighting" {
  return isXingshanDoubleLightingEquipment(equipment)
    ? "DoubleLighting"
    : "SingleLighting";
}

/** 会话内持久化灯位，刷新/切页后 SDK 回读 Open 时仍能恢复 UI */
export function buildXingshanLightingOpenStorageKey(deviceKey: string): string {
  return `${XINGSHAN_LIGHTING_OPEN_STORAGE_PREFIX}${String(deviceKey ?? "").trim()}`;
}

export function readPersistedXingshanLightingOpen(
  deviceKey: string
): XingshanLightingOpenStatus | null {
  if (typeof window === "undefined" || !deviceKey) return null;
  try {
    const stored = sessionStorage.getItem(
      buildXingshanLightingOpenStorageKey(deviceKey)
    );
    return isXingshanLightingOpenStatus(stored) ? stored : null;
  } catch {
    return null;
  }
}

export function persistXingshanLightingOpen(
  deviceKey: string,
  open: unknown
): void {
  if (typeof window === "undefined" || !deviceKey) return;
  const value = String(open ?? "");
  if (!isXingshanLightingOpenStatus(value)) return;
  try {
    sessionStorage.setItem(
      buildXingshanLightingOpenStorageKey(deviceKey),
      value
    );
  } catch {
    /* ignore */
  }
}

/** getDoubleLighting 数值 → open（固件常见：0 双灯位，1 上灯位，2 下灯位） */
export function mapDoubleLightingValueToOpen(
  value: unknown
): XingshanLightingOpenStatus | null {
  const n = Number(value);
  if (Number.isNaN(n)) return null;
  switch (n) {
    case 0:
      return "Open";
    case 1:
      return "OpenUp";
    case 2:
      return "OpenDown";
    default:
      return null;
  }
}

/** 恢复常规灯效时保留上下灯位 open，避免被统一压成 Open */
export function preserveXingshanDoubleLightingOpen(
  open: unknown,
  equipment?: XingshanKeyboardEquipment | Record<string, unknown>
): XingshanLightingOpenStatus {
  const value = String(open ?? "");
  if (value === "Close") return "Close";
  if (!isXingshanDoubleLightingEquipment(equipment)) {
    return "Open";
  }
  const locked = getXingshanDoubleLightingPositionLocked(equipment);
  if (locked) return openStatusForLockedDoubleLighting(locked);
  if (value === "OpenUp" || value === "OpenDown") {
    return value;
  }
  return "Open";
}

/**
 * 合并 SDK 回读的 open：设置上下灯后 get/set 响应常仍为 Open，
 * 需用最近一次下发的 open 或本地 prev 保留 OpenUp / OpenDown。
 */
export function resolveXingshanLightingOpenAfterSdkRead(
  sdkOpen: unknown,
  lastSubmittedOpen: unknown,
  previousOpen?: unknown,
  persistedOpen?: unknown,
  equipment?: XingshanKeyboardEquipment | Record<string, unknown>
): XingshanLightingOpenStatus {
  const sdk = String(sdkOpen ?? "");
  if (sdk === "Close") return "Close";

  let resolved: XingshanLightingOpenStatus;
  if (sdk === "OpenUp" || sdk === "OpenDown") {
    resolved = sdk;
  } else {
    const last = String(lastSubmittedOpen ?? "");
    // 用户明确下发双灯位 Open 时，不被历史 OpenUp/OpenDown 覆盖
    if (last === "Open") {
      resolved = sdk === "Close" ? "Close" : "Open";
    } else if (last === "OpenUp" || last === "OpenDown") {
      resolved = last;
    } else {
      const persisted = String(persistedOpen ?? "");
      if (persisted === "Open") {
        resolved = sdk === "Close" ? "Close" : "Open";
      } else if (persisted === "OpenUp" || persisted === "OpenDown") {
        resolved = persisted;
      } else {
        const prev = String(previousOpen ?? "");
        if (prev === "OpenUp" || prev === "OpenDown") {
          resolved = prev;
        } else {
          resolved = sdk === "Close" ? "Close" : "Open";
        }
      }
    }
  }

  return applyXingshanDoubleLightingPositionLock(resolved, equipment);
}
